import React, { useState } from "react";

import {
  AppBar,
  Toolbar,
  IconButton,
  Drawer,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  Container,
  Box,
  Link,
  Button,
  Badge
} from "@material-ui/core";
import MenuIcon from "@material-ui/icons/Menu";
import CloseIcon from "@material-ui/icons/Close";
import SettingsIcon from "@material-ui/icons/Settings";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import { Link as RouterLink } from "react-router-dom";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { useSelector } from "react-redux";
import { RootState } from "../app/rootReducer";
import { EQuoteType } from "../api/Types";
import { useAuth, boxShadow, colors } from "swyft-react-core";
import { FeaturePaymentDeposit, PermissionQuoteRead } from "../app/consts";
import { EQuoteStatus } from "../api/Quote/EQuoteStatus";
import ShoppingCartIcon from "@material-ui/icons/ShoppingCart";
import { TF } from "./typography/TF";
import OverrideTheme from "./OverrideTheme";
import { useConfig } from "./config/ConfigContext";

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    backgroundColor: "#fff",
    boxShadow: boxShadow
  },
  toolbar: {
    display: "flex",
    textTransform: "uppercase",
    [theme.breakpoints.up("md")]: {
      justifyContent: "space-around"
    }
  },
  logo: {
    [theme.breakpoints.down("sm")]: {
      left: "50%",
      position: "absolute",
      marginLeft: "-45px"
    },
    width: "90px"
  },
  logoOpen: {
    opacity: 0.1
  },
  mobileCart: {
    [theme.breakpoints.down("sm")]: {
      right: "2%",
      position: "absolute"
    }
  },
  smDrawer: {
    "&>div": {
      width: "100%",
      top: 50
    }
  },
  cartIcon: {
    color: colors.darkGrey
  }
}));

const drawerLinkStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    textAlign: "left",
    padding: "12px 0"
  },
  text: {
    flexGrow: 1,
    textDecoration: "none",
    textTransform: "uppercase"
  },
  icon: {
    color: colors.grey
  }
}));

interface INavBarState {
  sideMenuOpen: boolean;
  appBarPosition: "static" | "fixed";
}

export const NavBar = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isDesktopMenu = useMediaQuery(theme.breakpoints.up("md"));

  const { hasPermission } = useAuth();
  const { isFeatureEnabled } = useConfig();

  const { isAuthenticated, isLoading: authIsLoading, signOut } = useAuth();

  const [state, setState] = useState<INavBarState>({
    sideMenuOpen: false,
    appBarPosition: "static"
  });

  const { quote } = useSelector((state: RootState) => state.quote);

  const type = quote?.type ?? EQuoteType.Boiler;

  const toggleDrawer = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (
      event.type === "keydown" &&
      ((event as React.KeyboardEvent).key === "Tab" || (event as React.KeyboardEvent).key === "Shift")
    ) {
      return;
    }

    setState({
      ...state,
      sideMenuOpen: open,
      appBarPosition: open ? "fixed" : "static"
    });
  };

  var mdAppBar = (
    <>
      <Container maxWidth="lg">
        <Toolbar className={classes.toolbar}>
          <Box mr={4} flexGrow={2}>
            <img className={classes.logo} src={process.env.PUBLIC_URL + "/images/logo.png"} alt="Logo" />
          </Box>
          <Box flexGrow={2}>
            {isAuthenticated && (
              <Link component={RouterLink} to={"/quotes"}>
                <TF>All Quotes</TF>
              </Link>
            )}
          </Box>
          {isAuthenticated && hasPermission(PermissionQuoteRead) && quote && (
            <Box flexGrow={2}>
              <Link component={RouterLink} to={`/quote/edit/${quote.id}`}>
                <TF>Edit Quote</TF>
              </Link>
            </Box>
          )}
          <Box mx={2}>
            <Link component={RouterLink} to="/my-quotes">
              <TF>My Quotes</TF>
            </Link>
          </Box>
          <Box mx={2}>
            <Link component={RouterLink} to="/finance">
              <TF>Finance Options</TF>
            </Link>
          </Box>
          <Box mx={2}>
            <Link component={RouterLink} to="/payment">
              <TF>Payment Details</TF>
            </Link>
          </Box>
          <Box mx={2}>
            {type === EQuoteType.Boiler && (
              <Link component={RouterLink} to="/protect">
                <TF>Servicing</TF>
              </Link>
            )}
          </Box>
          <Box>
            {quote && isFeatureEnabled(FeaturePaymentDeposit) && quote.status === EQuoteStatus.Sent && (
              <IconButton aria-label="menu" component={RouterLink} to={`/quote/cart/${quote.id}`} title="Cart">
                <Badge color="error" badgeContent=" " variant="dot">
                  <ShoppingCartIcon className={classes.cartIcon} />
                </Badge>
              </IconButton>
            )}
          </Box>
          {isAuthenticated && !authIsLoading && (
            <Box mx={2}>
              <Button onClick={() => signOut()}>
                <TF upperCase>Logout</TF>
              </Button>
            </Box>
          )}
        </Toolbar>
      </Container>
    </>
  );
  var smAppBar = (
    <Toolbar className={classes.toolbar}>
      <IconButton edge="start" color="primary" aria-label="menu" onClick={toggleDrawer(!state.sideMenuOpen)}>
        {state.sideMenuOpen ? <CloseIcon /> : <MenuIcon />}
      </IconButton>
      <img
        className={`${classes.logo} ${state.sideMenuOpen ? classes.logoOpen : ""}`}
        src="images/logo.png"
        alt="Logo"
      />
      {quote && isFeatureEnabled(FeaturePaymentDeposit) && quote.status === EQuoteStatus.Sent && (
        <IconButton
          className={classes.mobileCart}
          aria-label="menu"
          component={RouterLink}
          to={`/quote/cart/${quote.id}`}
          title="Cart"
        >
          <Badge color="error" badgeContent=" " variant="dot">
            <ShoppingCartIcon className={classes.cartIcon} />
          </Badge>
        </IconButton>
      )}
    </Toolbar>
  );

  var smDrawer = (
    <Drawer open={state.sideMenuOpen} onClose={toggleDrawer(false)} className={classes.smDrawer} variant="persistent">
      {isAuthenticated && (
        <>
          <List>
            <ListItem button>
              <ListItemIcon>
                <SettingsIcon />
              </ListItemIcon>
              <ListItemText>
                <Button component={RouterLink} to={"/quotes"} onClick={toggleDrawer(false)}>
                  <TF>All Quotes</TF>
                </Button>
              </ListItemText>
            </ListItem>
            {hasPermission(PermissionQuoteRead) && quote && (
              <ListItem button>
                <ListItemIcon>
                  <SettingsIcon />
                </ListItemIcon>
                <ListItemText>
                  <Button component={RouterLink} to={`/quote/edit/${quote.id}`} onClick={toggleDrawer(false)}>
                    <TF>Edit Quote</TF>
                  </Button>
                </ListItemText>
              </ListItem>
            )}
            {!authIsLoading && (
              <ListItem button>
                <ListItemIcon>
                  <ExitToAppIcon />
                </ListItemIcon>
                <ListItemText>
                  <Button onClick={() => signOut()}>
                    <TF upperCase>Logout</TF>
                  </Button>
                </ListItemText>
              </ListItem>
            )}
          </List>
          <Divider />
        </>
      )}
      <Box textAlign="center" p={4} width="300px" margin="0 auto">
        <DrawerLink text="My Quotes" href="/my-quotes" handleClick={toggleDrawer(false)} />
        <DrawerLink text="Finance Options" href="/finance" handleClick={toggleDrawer(false)} />
        <DrawerLink text="Payment Details" href="/payment" handleClick={toggleDrawer(false)} />
        {type === EQuoteType.Boiler && (
          <DrawerLink text="Servicing" href="/protect" handleClick={toggleDrawer(false)} />
        )}
      </Box>
    </Drawer>
  );

  return (
    <OverrideTheme>
      <AppBar position={state.appBarPosition} className={classes.root}>
        {isDesktopMenu ? mdAppBar : smAppBar}
      </AppBar>
      {!isDesktopMenu && smDrawer}
    </OverrideTheme>
  );
};

interface DrawerLinkProps {
  text: string;
  href: string;
  handleClick: (_: any) => void;
}

const DrawerLink = ({ text, href, handleClick }: DrawerLinkProps) => {
  const classes = drawerLinkStyles();
  return (
    <Link component={RouterLink} to={href} onClick={handleClick} className={classes.root}>
      <TF className={classes.text}>{text}</TF>
      <ChevronRightIcon className={classes.icon} />
    </Link>
  );
};
